import { useState, useMemo } from "react";
import { Filter, Search, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * PresenceFilterPopover — presence tri-state (Tümü / Var / Yok) combined with
 * an optional multi-select of specific IDs. Used by the Products page for
 * filters where a merchant can either narrow by "is/isn't set at all" or
 * pick specific entries (or both — "Var + specific" is treated as an AND).
 *
 * value shape: { presence: "all" | "has" | "none" | <custom>, ids: string[] }
 *
 * When presence === "none" (or any of the `extraStates` that opts in via
 * `disablesList: true`) the specific list is disabled — there's nothing to
 * pick because the axis is empty by definition. AND semantics belong to the
 * caller's filter logic.
 *
 * `extraStates` lets specific filters extend the tri-state without changing
 * the API for other consumers. Each extra state is a `{ key, label, disablesList }`
 * — the Products page's Tablo filter uses this to add a "Muaf" state for
 * `sizingTableExempt=true`.
 */
export const PresenceFilterPopover = ({
    label, presenceLabels, options, value, onChange, keyOf, labelOf,
    searchOf, renderOption, emptyLabel, testId, recentLimit = 5,
    extraStates,
}) => {
    const [query, setQuery] = useState("");
    const matches = searchOf || labelOf;
    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options.slice(-recentLimit).reverse();
        return options.filter((o) => matches(o).toLowerCase().includes(q));
    }, [options, query, matches, recentLimit]);
    const isRecency = !query.trim() && options.length > recentLimit;
    const presence = value.presence || "all";
    const ids = value.ids || [];
    const states = [
        { key: "all", label: "Tümü" },
        { key: "has", label: presenceLabels?.has || "Var" },
        { key: "none", label: presenceLabels?.none || "Yok", disablesList: true },
        ...(extraStates || []),
    ];
    const currentState = states.find((s) => s.key === presence);
    const listDisabled = !!currentState?.disablesList;
    const active = presence !== "all" || ids.length > 0;
    const toggle = (k) => {
        const next = new Set(ids);
        if (next.has(k)) next.delete(k); else next.add(k);
        onChange({ presence, ids: [...next] });
    };
    // Trigger label — succinct summary of current state.
    let triggerBadge = null;
    if (presence !== "all" && ids.length === 0) triggerBadge = currentState?.label || presence;
    else if (ids.length > 0) triggerBadge = String(ids.length);
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`h-9 inline-flex items-center gap-1.5 rounded-md border px-3 text-xs transition-colors ${active ? "border-foreground/40 bg-muted/60 text-foreground" : "border-border bg-surface text-muted-foreground hover:text-foreground"}`}
                    data-testid={testId}
                >
                    <Filter className="h-3 w-3" />
                    <span>{label}</span>
                    {triggerBadge != null && (
                        <span className="rounded-md bg-brand text-brand-fg px-1.5 py-0.5 text-[10px] font-mono">{triggerBadge}</span>
                    )}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-0 overflow-hidden">
                <div className="p-2 border-b border-border">
                    <div className={`inline-flex w-full rounded-md border border-border p-0.5 bg-surface ${states.length > 3 ? "grid" : ""}`} style={states.length > 3 ? { gridTemplateColumns: `repeat(${states.length}, 1fr)` } : undefined} data-testid={`${testId}-presence-toggle`}>
                        {states.map((opt) => (
                            <button
                                key={opt.key}
                                onClick={() => onChange({ presence: opt.key, ids: opt.disablesList ? [] : ids })}
                                className={`h-7 px-2 text-[11px] font-medium rounded-sm transition-colors ${states.length > 3 ? "" : "flex-1"} ${presence === opt.key ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                                data-testid={`${testId}-presence-${opt.key}`}
                                aria-pressed={presence === opt.key}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={`p-2 border-b border-border ${listDisabled ? "opacity-40 pointer-events-none" : ""}`}>
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ara…" className="h-8 pl-8 text-xs bg-background" data-testid={`${testId}-search`} />
                    </div>
                </div>
                {isRecency && !listDisabled && (
                    <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground/80" data-testid={`${testId}-recent-hint`}>
                        Son eklenen {Math.min(recentLimit, options.length)} · Daha fazlası için ara
                    </div>
                )}
                <div className={`max-h-56 overflow-y-auto scroll-thin ${listDisabled ? "opacity-40 pointer-events-none" : ""}`}>
                    {visible.length === 0 ? (
                        <div className="px-3 py-6 text-center text-[11px] text-muted-foreground">{emptyLabel || "Sonuç yok"}</div>
                    ) : visible.map((o) => {
                        const k = keyOf(o);
                        const checked = ids.includes(k);
                        return (
                            <label
                                key={k}
                                className={`flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-muted/40 ${checked ? "bg-muted/30" : ""}`}
                                data-testid={`${testId}-option-${k}`}
                            >
                                <Checkbox checked={checked} onCheckedChange={() => toggle(k)} />
                                {renderOption ? renderOption(o) : <span className="truncate">{labelOf(o)}</span>}
                            </label>
                        );
                    })}
                </div>
                {active && (
                    <div className="border-t border-border p-2 bg-muted/20">
                        <button
                            onClick={() => onChange({ presence: "all", ids: [] })}
                            className="text-[11px] text-muted-foreground hover:text-foreground w-full text-left"
                            data-testid={`${testId}-clear`}
                        >
                            Temizle
                        </button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};
